import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, Loader2, AlertCircle, Check, X, CheckCircle, Shield, ArrowLeft } from 'lucide-react';
import { useToast } from '../../contexts/NotificationContext';
import GoogleSignInButton from '../GoogleSignInButton';
import { isGoogleOAuthAvailable } from '../../config/amplify-config';

interface AuthFormProps {
  onSuccess?: () => void;
  onStateChange?: (state: { isSignUp: boolean; showConfirmation: boolean }) => void;
  compact?: boolean;
}

const AuthForm: React.FC<AuthFormProps> = ({ onSuccess, onStateChange, compact = false }) => {
  const {
    register,
    login,
    confirmRegistration,
    resendConfirmation,
    error,
    clearError,
    success,
    clearSuccess,
    needsConfirmation,
    updateNeedsConfirmation,
    mfaChallenge,
    verifyMFACode,
    clearMFAChallenge, // Add this function to your AuthContext if not already present
    requestPasswordReset,
    confirmPasswordReset
  } = useAuth();

  const toast = useToast();

  const [isSignUp, setIsSignUp] = useState(window.location.hash !== '#login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(!compact);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    confirmationCode: '',
    mfaCode: '',
    resetCode: '',
    newPassword: ''
  });

  const [formErrors, setFormErrors] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    confirmationCode: '',
    mfaCode: '',
    resetCode: '',
    newPassword: ''
  });

  // Handle confirmation state - this is the MOST IMPORTANT effect
  useEffect(() => {
    if (needsConfirmation) {
      console.log('needsConfirmation detected in AuthForm:', needsConfirmation);
      // Force the form into confirmation mode
      setShowConfirmation(true);
      setConfirmationEmail(needsConfirmation);

      // Ensure we're in sign up mode
      if (!isSignUp) {
        setIsSignUp(true);
      }

      // Notify parent component
      onStateChange?.({ isSignUp: true, showConfirmation: true });
    }
  }, [needsConfirmation, isSignUp, onStateChange]);

  // Debug current state
  useEffect(() => {
    console.log('AuthForm current state:', {
      isSignUp,
      showConfirmation,
      confirmationEmail,
      needsConfirmation,
      mfaChallenge,
      showForgotPassword,
      showResetConfirm
    });
  }, [isSignUp, showConfirmation, confirmationEmail, needsConfirmation, mfaChallenge, showForgotPassword, showResetConfirm]);

  // Track previous state to prevent unnecessary updates
  const prevStateRef = useRef({ isSignUp: true, showConfirmation: false });

  // Notify parent of state changes only when they actually change
  useEffect(() => {
    const currentState = { isSignUp, showConfirmation };
    const prevState = prevStateRef.current;

    if (prevState.isSignUp !== currentState.isSignUp ||
      prevState.showConfirmation !== currentState.showConfirmation) {
      onStateChange?.(currentState);
      prevStateRef.current = currentState;
    }
  }, [isSignUp, showConfirmation, onStateChange]); // Include onStateChange in dependencies

  const validateForm = () => {
    const errors = { fullName: '', email: '', password: '', confirmPassword: '', confirmationCode: '', mfaCode: '', resetCode: '', newPassword: '' };
    let isValid = true;

    // If we're in password reset confirm mode
    if (showResetConfirm) {
      if (!formData.resetCode.trim()) {
        errors.resetCode = 'Reset code is required';
        isValid = false;
      }
      if (!formData.newPassword) {
        errors.newPassword = 'New password is required';
        isValid = false;
      } else if (formData.newPassword.length < 8) {
        errors.newPassword = 'Password must be at least 8 characters long';
        isValid = false;
      }
      
      setFormErrors(errors);
      return isValid;
    }

    // If we're in forgot password mode
    if (showForgotPassword) {
      if (!formData.email.trim()) {
        errors.email = 'Email is required';
        isValid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
        isValid = false;
      }
      
      setFormErrors(errors);
      return isValid;
    }

    // If we're in MFA challenge mode, only validate the MFA code
    if (mfaChallenge?.isRequired) {
      if (!formData.mfaCode.trim()) {
        errors.mfaCode = 'Verification code is required';
        isValid = false;
      } else if (formData.mfaCode.trim().length < 6) {
        errors.mfaCode = 'Verification code must be at least 6 digits';
        isValid = false;
      }
      
      setFormErrors(errors);
      return isValid;
    }

    // If we're in confirmation mode, we only need to validate the confirmation code
    if (showConfirmation) {
      if (!formData.confirmationCode.trim()) {
        errors.confirmationCode = 'Confirmation code is required';
        isValid = false;
      }

      // Set form errors and return validation result
      setFormErrors(errors);
      return isValid;
    }


    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    if (!formData.password) {
      errors.password = 'Password is required';
      isValid = false;
    } else if (isSignUp) {
      const hasMinLength = formData.password.length >= 8;
      const hasUpperCase = /[A-Z]/.test(formData.password);
      const hasNumber = /[0-9]/.test(formData.password);
      const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password);
      
      if (!hasMinLength || !hasUpperCase || !hasNumber || !hasSpecialChar) {
        errors.password = 'Password does not meet requirements';
        isValid = false;
      }
    }

    // Validate confirm password for sign up
    if (isSignUp) {
      if (!formData.confirmPassword) {
        errors.confirmPassword = 'Please confirm your password';
        isValid = false;
      } else if (formData.confirmPassword !== formData.password) {
        errors.confirmPassword = 'Passwords do not match';
        isValid = false;
      }
    }

    if (showConfirmation && !formData.confirmationCode.trim()) {
      errors.confirmationCode = 'Confirmation code is required';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear errors when user starts typing
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    clearError();
    clearSuccess();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Handle password reset confirmation
      if (showResetConfirm) {
        await confirmPasswordReset(formData.email, formData.resetCode, formData.newPassword);
        // On success, go back to sign in
        setShowForgotPassword(false);
        setShowResetConfirm(false);
        setIsSignUp(false);
        setFormData(prev => ({ ...prev, resetCode: '', newPassword: '' }));
        return;
      }

      // Handle password reset request
      if (showForgotPassword) {
        await requestPasswordReset(formData.email);
        setShowResetConfirm(true);
        return;
      }

      // Handle MFA challenge
      if (mfaChallenge?.isRequired) {
        console.log(`Submitting MFA code for ${mfaChallenge.challengeType} challenge`);
        try {
          await verifyMFACode(formData.mfaCode);
          // The verifyMFACode function in AuthContext will handle the success flow, including navigation
        } catch (err) {
          console.error('MFA verification error:', err);
          // Error is handled by AuthContext
        }
        return;
      }
      if (showConfirmation) {
        console.log('Submitting confirmation code for:', confirmationEmail, 'Code:', formData.confirmationCode);

        // Make sure we have both email and code
        if (!confirmationEmail || !formData.confirmationCode) {
          console.error('Missing email or confirmation code');
          setFormErrors(prev => ({
            ...prev,
            confirmationCode: !formData.confirmationCode ? 'Confirmation code is required' : ''
          }));
          return;
        }

        // Call the confirmation function
        try {
          await confirmRegistration(confirmationEmail, formData.confirmationCode);
          
          // Only proceed if there was no error
          // Success message indicates confirmation was successful, but we'll keep the user on the same screen
          // with a button to proceed to login instead of automatically transitioning
          if (success && !error) {
            // After successful confirmation
            console.log('Confirmation completed successfully');
            // We no longer automatically transition to sign in
            // Just clear the confirmation code but keep the user on the confirmation screen
            // The success message will be displayed along with a button to go to sign in
            updateNeedsConfirmation(null); // Clear the confirmation state in context
            setFormData(prev => ({ ...prev, confirmationCode: '' }));
          } else {
            // If there was an error, keep the user in confirmation view
            console.log('Confirmation had an error, keeping in confirmation view');
          }
        } catch (err) {
          // Error is already handled in the confirmRegistration function
          // Just log it here for debugging
          console.log('Error caught in handleSubmit during confirmation:', err);
          // Keep the user in confirmation view
        }
      } else if (isSignUp) {
        console.log('Attempting to register user:', formData.email);
        try {
          const result = await register(formData.email, formData.password);
          console.log('Registration result:', result);

          if (result.requiresConfirmation) {
            console.log('Registration requires confirmation, updating state...');
            // Important: Store email before showing confirmation screen
            const emailForConfirmation = formData.email;

            // CRITICAL FIX: Use the AuthContext's updateNeedsConfirmation function
            // This will ensure the state is preserved in the context
            console.log('Setting needsConfirmation in AuthContext to:', emailForConfirmation);
            updateNeedsConfirmation(emailForConfirmation);

            // Also update local state
            setConfirmationEmail(emailForConfirmation);
            setShowConfirmation(true);

            // Force state update to complete before continuing
            await new Promise(resolve => setTimeout(resolve, 100));

            console.log('Confirmation state after update:', {
              showConfirmation: true,
              email: emailForConfirmation,
              needsConfirmationSet: true
            });

            // Notify parent component about state change
            onStateChange?.({ isSignUp: true, showConfirmation: true });

            // Don't call onSuccess yet since we need confirmation
            return;
          }
        } catch (registerError) {
          console.error('Registration specific error:', registerError);
          // Continue to the general error handler
          throw registerError;
        }
      } else {
        await login(formData.email, formData.password);
      }

      // Call onSuccess callback if provided
      onSuccess?.();
    } catch (err) {
      // Error is handled by AuthContext
      console.error('Authentication error:', err);
      
      // Check if this is an access denied error from preSignUp Lambda
      if (err instanceof Error && err.message.includes('Access denied')) {
        // Show themed toast for access denied
        toast.error('Access Request Required', {
          duration: 6000, // Show longer for important message
          style: {
            background: 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)',
            color: '#FFFFFF',
            border: '1px solid #FFCDD2',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '14px',
            fontWeight: '500',
            boxShadow: '0 8px 32px rgba(244, 67, 54, 0.3)',
          }
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (!confirmationEmail) return;

    setIsResending(true);
    try {
      await resendConfirmation(confirmationEmail);
    } catch (err) {
      console.error('Error resending code:', err);
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToSignUp = () => {
    // Clear the needsConfirmation state to go back to signup form
    updateNeedsConfirmation(null);
    setShowConfirmation(false);
    clearError();
    clearSuccess();
    // Reset form data but keep the email
    setFormData(prev => ({
      ...prev,
      confirmationCode: '',
      // Keep email and fullName if they exist
      email: prev.email,
      fullName: prev.fullName,
      password: '' // Clear password for security
    }));
    // Ensure we're in sign up mode
    setIsSignUp(true);
    // Notify parent component
    onStateChange?.({ isSignUp: true, showConfirmation: false });
  };

  const toggleMode = () => {
    const newMode = !isSignUp;
    setIsSignUp(newMode);
    window.location.hash = newMode ? '' : '#login';
    setShowConfirmation(false);
    setShowForgotPassword(false);
    setShowResetConfirm(false);
    setFormData({
      fullName: '',
      email: '',
      password: '',
      confirmPassword: '',
      confirmationCode: '',
      mfaCode: '',
      resetCode: '',
      newPassword: ''
    });
    setFormErrors({
      fullName: '',
      email: '',
      password: '',
      confirmPassword: '',
      confirmationCode: '',
      mfaCode: '',
      resetCode: '',
      newPassword: ''
    });
    clearError();
    clearSuccess();
    // Clear MFA challenge if the function exists
    if (typeof clearMFAChallenge === 'function') {
      clearMFAChallenge();
    }
  };

  const handleBackFromMFA = () => {
    // Clear MFA challenge state
    if (typeof clearMFAChallenge === 'function') {
      clearMFAChallenge();
    }
    // Reset form
    setFormData(prev => ({ ...prev, mfaCode: '' }));
    clearError();
    clearSuccess();
  };

  const getTitle = () => {
    if (showResetConfirm) return 'Reset Your Password';
    if (showForgotPassword) return 'Forgot Password';
    if (mfaChallenge?.isRequired) return 'Two-Factor Authentication';
    if (showConfirmation) return 'Confirm Your Account';
    return isSignUp ? 'Create Your Account' : 'Welcome Back';
  };

  const getSubtitle = () => {
    if (showResetConfirm) return 'Enter the code sent to your email and your new password';
    if (showForgotPassword) return 'Enter your email to receive a reset code';
    if (mfaChallenge?.isRequired) return `Enter your ${mfaChallenge.challengeType === 'TOTP' ? 'authenticator app' : 'SMS'} verification code`;
    if (showConfirmation) return 'Enter the confirmation code sent to your email';
    return isSignUp
      ? 'Join Kolega AI, start creating without limits...'
      : 'Log in, continue your creation journey';
  };

  // If in compact mode and not showing email form, show initial view
  if (compact && !showEmailForm && !showConfirmation && !showForgotPassword && !showResetConfirm && !mfaChallenge?.isRequired) {
    return (
      <div className="bg-slate-100 dark:bg-neutral-900 rounded-2xl shadow-xl border-2 border-lime-500 p-8">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-ui-text-primary mb-2">
            <span className="text-ui-text-primary">Kolega</span>
            <span className="text-brand-primary">.ai</span>
          </h1>
          <h2 className="text-xl font-semibold text-ui-text-primary mb-2">
            {isSignUp ? 'Create Your Account' : 'Welcome Back'}
          </h2>
          <p className="text-ui-text-secondary text-sm">
            {isSignUp ? 'Join Kolega AI, start creating without limits...' : 'Log in, continue your creation journey'}
          </p>
        </div>
        
        <div className="space-y-4">
          {/* Google Sign-In Button */}
          {isGoogleOAuthAvailable() && (
            <GoogleSignInButton
              disabled={isSubmitting}
              className="w-full"
            />
          )}
          
          {/* Or Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-ui-border-primary dark:border-neutral-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-slate-100 dark:bg-neutral-900 text-ui-text-secondary">or</span>
            </div>
          </div>
          
          {/* Continue with Email Button */}
          <button
            type="button"
            onClick={() => setShowEmailForm(true)}
            className="w-full py-3 px-4 border border-transparent rounded-xl text-sm font-medium text-black bg-brand-primary hover:bg-brand-primaryDark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-colors"
          >
            Continue with email
          </button>
        </div>
        
        {/* Toggle mode link */}
        <div className="mt-6 text-center">
          <p className="text-sm text-ui-text-secondary">
            {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              type="button"
              onClick={toggleMode}
              className="font-medium text-brand-primary hover:text-brand-primaryDark transition-colors"
            >
              {isSignUp ? 'Sign in' : 'Sign up'}
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-100 dark:bg-neutral-900 rounded-2xl shadow-xl border-2 border-lime-500 p-8">
      {/* Hero Section - Logo and Title */}
      <div className="text-center mb-12">
        {/* Kolega.Studio Logo */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-ui-text-primary">
            <span className="text-ui-text-primary">Kolega</span>
            <span className="text-brand-primary">.Studio</span>
          </h1>
        </div>

        {/* Title and Subtitle */}
        <h2 className="text-3xl font-bold text-ui-text-primary mb-2">
          {getTitle()}
        </h2>
        <p className="text-ui-text-secondary mb-6">
          {getSubtitle()}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Success Message */}
        {success && (
          <div className="flex items-center p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
            <p className="text-sm text-green-700 dark:text-green-300">{success}</p>
          </div>
        )}
        
        {/* Error Message */}
        {error && (
          <div className="flex items-center p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Google Sign-In Button & OR divider - Show only when OAuth is configured and for normal sign-in/sign-up flows */}
        {!showConfirmation && !showForgotPassword && !showResetConfirm && !mfaChallenge?.isRequired && isGoogleOAuthAvailable() && (
          <>
            <GoogleSignInButton
              disabled={isSubmitting}
              className="w-full"
            />
            
            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-ui-border-primary dark:border-neutral-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-slate-100 dark:bg-neutral-900 text-ui-text-secondary">or</span>
              </div>
            </div>
          </>
        )}

        {showResetConfirm ? (
          /* Password Reset Confirmation */
          <>
            <div>
              <label htmlFor="resetCode" className="block text-sm font-medium text-ui-text-primary mb-2">
                Reset Code
              </label>
              <input
                id="resetCode"
                name="resetCode"
                type="text"
                value={formData.resetCode}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-xl text-ui-text-primary bg-ui-background-primary dark:bg-neutral-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-colors ${
                  formErrors.resetCode ? 'border-red-500' : 'border-ui-border-primary dark:border-neutral-700'
                }`}
                placeholder="Enter 6-digit code"
                maxLength={6}
              />
              {formErrors.resetCode && (
                <p className="mt-1 text-sm text-red-500">{formErrors.resetCode}</p>
              )}
            </div>
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-ui-text-primary mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  id="newPassword"
                  name="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-xl text-ui-text-primary bg-ui-background-primary dark:bg-neutral-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-colors pr-12 ${
                    formErrors.newPassword ? 'border-red-500' : 'border-ui-border-primary dark:border-neutral-700'
                  }`}
                  placeholder="Create a new password (min. 8 characters)"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-ui-text-secondary hover:text-ui-text-primary transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {formErrors.newPassword && (
                <p className="mt-1 text-sm text-red-500">{formErrors.newPassword}</p>
              )}
            </div>
          </>
        ) : showForgotPassword ? (
          /* Forgot Password Email Input */
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-ui-text-primary mb-2">
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border rounded-xl text-ui-text-primary bg-ui-background-primary dark:bg-neutral-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-colors ${
                formErrors.email ? 'border-red-500' : 'border-ui-border-primary dark:border-neutral-700'
              }`}
              placeholder="Enter your email"
            />
            {formErrors.email && (
              <p className="mt-1 text-sm text-red-500">{formErrors.email}</p>
            )}
          </div>
        ) : mfaChallenge?.isRequired ? (
          /* MFA Code Input */
          <div>
            <div className="flex items-center justify-center mb-6">
              <div className="p-3 bg-brand-primary/20 rounded-full">
                <Shield className="h-8 w-8 text-brand-primary" />
              </div>
            </div>
            <label htmlFor="mfaCode" className="block text-sm font-medium text-ui-text-primary mb-2">
              {mfaChallenge.challengeType === 'TOTP' ? 'Authentication App Code' : 'SMS Verification Code'}
            </label>
            <input
              id="mfaCode"
              name="mfaCode"
              type="text"
              inputMode="numeric"
              value={formData.mfaCode}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border rounded-xl text-ui-text-primary bg-ui-background-primary dark:bg-neutral-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-colors ${formErrors.mfaCode ? 'border-red-500' : 'border-ui-border-primary dark:border-neutral-700'
                }`}
              placeholder="Enter 6-digit code"
              maxLength={6}
              autoFocus
            />
            {formErrors.mfaCode && (
              <p className="mt-1 text-sm text-red-500">{formErrors.mfaCode}</p>
            )}
            <p className="mt-2 text-xs text-ui-text-secondary">
              {mfaChallenge.challengeType === 'TOTP' 
                ? 'Enter the code from your authentication app (e.g., Google Authenticator)'
                : 'Enter the code sent to your phone'}
            </p>
          </div>
        ) : showConfirmation ? (
          /* Confirmation Code Input */
          <div>
            <label htmlFor="confirmationCode" className="block text-sm font-medium text-ui-text-primary mb-2">
              Confirmation Code
            </label>
            <input
              id="confirmationCode"
              name="confirmationCode"
              type="text"
              value={formData.confirmationCode}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border rounded-xl text-ui-text-primary ${!!success ? 'bg-ui-background-secondary cursor-not-allowed opacity-75' : 'bg-ui-background-primary'} focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-colors ${formErrors.confirmationCode ? 'border-red-500' : 'border-ui-border-primary'
                }`}
              placeholder="Enter 6-digit code"
              maxLength={6}
            />
            {formErrors.confirmationCode && (
              <p className="mt-1 text-sm text-red-500">{formErrors.confirmationCode}</p>
            )}
            <p className="mt-2 text-xs text-ui-text-secondary">
              Code sent to: {confirmationEmail}
            </p>
          </div>
        ) : (
          <>
            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-ui-text-primary mb-2">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                autoComplete="username"
                className={`w-full px-4 py-3 border rounded-xl text-ui-text-primary bg-ui-background-primary dark:bg-neutral-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-colors ${formErrors.email ? 'border-red-500' : 'border-ui-border-primary dark:border-neutral-700'
                }`}
                placeholder="Enter your email"
              />
              {formErrors.email && (
                <p className="mt-1 text-sm text-red-500">{formErrors.email}</p>
              )}
            </div>

            {/* Password Input */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label htmlFor="password" className="block text-sm font-medium text-ui-text-primary">
                  Password
                </label>
                {!isSignUp && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(true);
                      clearError();
                      clearSuccess();
                    }}
                    className="text-sm text-brand-primary hover:text-brand-primaryDark transition-colors"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                  className={`w-full px-4 py-3 border rounded-xl text-ui-text-primary bg-ui-background-primary dark:bg-neutral-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-colors pr-12 ${formErrors.password ? 'border-red-500' : 'border-ui-border-primary dark:border-neutral-700'
                  }`}
                  placeholder={isSignUp ? 'Create a password' : 'Enter your password'}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-ui-text-secondary hover:text-ui-text-primary transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {formErrors.password && (
                <p className="mt-1 text-sm text-red-500">{formErrors.password}</p>
              )}
            </div>

            {/* Password Criteria (Sign Up Only) */}
            {isSignUp && formData.password && (
              <div className="mt-2 p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
                <p className="text-xs font-medium text-ui-text-secondary mb-2">Password must:</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    {formData.password.length >= 8 ? (
                      <Check className="w-3 h-3 text-green-600 dark:text-green-500" />
                    ) : (
                      <X className="w-3 h-3 text-red-500 dark:text-red-400" />
                    )}
                    <span className={`text-xs ${
                      formData.password.length >= 8
                        ? 'text-green-600 dark:text-green-500'
                        : 'text-ui-text-secondary'
                    }`}>
                      Be at least 8 characters long
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/[A-Z]/.test(formData.password) ? (
                      <Check className="w-3 h-3 text-green-600 dark:text-green-500" />
                    ) : (
                      <X className="w-3 h-3 text-red-500 dark:text-red-400" />
                    )}
                    <span className={`text-xs ${
                      /[A-Z]/.test(formData.password)
                        ? 'text-green-600 dark:text-green-500'
                        : 'text-ui-text-secondary'
                    }`}>
                      Contain at least one uppercase letter
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/[0-9]/.test(formData.password) ? (
                      <Check className="w-3 h-3 text-green-600 dark:text-green-500" />
                    ) : (
                      <X className="w-3 h-3 text-red-500 dark:text-red-400" />
                    )}
                    <span className={`text-xs ${
                      /[0-9]/.test(formData.password)
                        ? 'text-green-600 dark:text-green-500'
                        : 'text-ui-text-secondary'
                    }`}>
                      Contain at least one number
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password) ? (
                      <Check className="w-3 h-3 text-green-600 dark:text-green-500" />
                    ) : (
                      <X className="w-3 h-3 text-red-500 dark:text-red-400" />
                    )}
                    <span className={`text-xs ${
                      /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password)
                        ? 'text-green-600 dark:text-green-500'
                        : 'text-ui-text-secondary'
                    }`}>
                      Contain at least one special character
                    </span>
                  </div>
                  {formData.confirmPassword && (
                    <div className="flex items-center gap-2">
                      {formData.password === formData.confirmPassword ? (
                        <Check className="w-3 h-3 text-green-600 dark:text-green-500" />
                      ) : (
                        <X className="w-3 h-3 text-red-500 dark:text-red-400" />
                      )}
                      <span className={`text-xs ${
                        formData.password === formData.confirmPassword
                          ? 'text-green-600 dark:text-green-500'
                          : 'text-red-500 dark:text-red-400'
                      }`}>
                        Passwords match
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Confirm Password Input (Sign Up Only) */}
            {isSignUp && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-ui-text-primary mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    className={`w-full px-4 py-3 border rounded-xl text-ui-text-primary bg-ui-background-primary dark:bg-neutral-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-colors pr-12 ${
                      formData.confirmPassword && formData.password !== formData.confirmPassword
                        ? 'border-red-500'
                        : formData.confirmPassword && formData.password === formData.confirmPassword
                        ? 'border-green-500'
                        : 'border-ui-border-primary dark:border-neutral-700'
                    }`}
                    placeholder="Re-enter your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-ui-text-secondary hover:text-ui-text-primary transition-colors"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Submit Button */}
        {!(showConfirmation && success) && (
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl text-sm font-medium text-black bg-brand-primary hover:bg-brand-primaryDark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                {showResetConfirm ? 'Resetting Password...' : showForgotPassword ? 'Sending Reset Code...' : showConfirmation ? 'Confirming...' : isSignUp ? 'Signing Up...' : 'Signing In...'}
              </span>
            ) : showResetConfirm ? (
              'Reset Password'
            ) : showForgotPassword ? (
              'Send Reset Code'
            ) : showConfirmation ? (
              'Confirm Account'
            ) : isSignUp ? (
              'Sign Up'
            ) : (
              'Sign In'
            )}
          </button>
        )}

        {/* Back button for MFA */}
        {mfaChallenge?.isRequired && (
          <button
            type="button"
            onClick={handleBackFromMFA}
            className="w-full py-2 px-4 text-sm text-ui-text-primary dark:text-brand-primary hover:text-brand-primaryDark transition-colors flex items-center justify-center"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Sign In
          </button>
        )}

        {/* Resend Code Button, Back to Sign Up Button, or Go to Sign In Button (Confirmation Only) */}
        {showConfirmation && (
          <div className="space-y-4">
            {success ? (
              <button
                type="button"
                onClick={() => {
                  // Switch to sign in mode
                  setIsSignUp(false);
                  setShowConfirmation(false);
                  clearSuccess(); // Clear the success message
                  // Notify parent component
                  onStateChange?.({ isSignUp: false, showConfirmation: false });
                }}
                className="w-full py-3 px-4 text-sm font-medium text-black bg-brand-primary hover:bg-brand-primaryDark rounded-xl transition-colors flex justify-center items-center"
              >
                Go to Sign In
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={isResending || isSubmitting}
                  className="w-full py-2 px-4 text-sm text-ui-text-primary dark:text-brand-primary hover:text-brand-primaryDark border border-ui-border-primary dark:border-brand-primary hover:border-brand-primaryDark rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center"
                >
                  {isResending ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="w-4 h-4 animate-spin mr-2 text-ui-text-primary dark:text-brand-primary" />
                      Sending...
                    </span>
                  ) : (
                    'Resend Code'
                  )}
                </button>
                
                {/* Back to Sign Up Button */}
                <button
                  type="button"
                  onClick={handleBackToSignUp}
                  disabled={isSubmitting || isResending}
                  className="w-full py-2 px-4 text-sm text-ui-text-primary dark:text-brand-primary hover:text-brand-primaryDark transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Sign Up
                </button>
              </>
            )}
          </div>
        )}

        {/* Back button for forgot password flow */}
        {(showForgotPassword || showResetConfirm) && (
          <button
            type="button"
            onClick={() => {
              setShowForgotPassword(false);
              setShowResetConfirm(false);
              clearError();
              clearSuccess();
            }}
            className="w-full py-2 px-4 text-sm text-ui-text-primary dark:text-brand-primary hover:text-brand-primaryDark transition-colors flex items-center justify-center"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Sign In
          </button>
        )}
      </form>

      {/* Toggle Between Sign In / Sign Up */}
      {!showConfirmation && !showForgotPassword && !showResetConfirm && !mfaChallenge?.isRequired && (
        <div className="mt-6 text-center">
          <p className="text-sm text-ui-text-secondary">
            {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              type="button"
              onClick={toggleMode}
              className="font-medium text-brand-primary hover:text-brand-primaryDark transition-colors"
            >
              {isSignUp ? 'Sign in' : 'Sign up'}
            </button>
          </p>
        </div>
      )}
    </div>
  );
};

export default AuthForm;
